<script>
import { requiredIf, required } from 'vuelidate/lib/validators';
import ContentBlockMixin from '@shared/mixins/ContentBlock';
import ContentBlockScratchCardView from '@shared/components/ContentBlocks/Views/ScratchCard.vue';
import TiptapEditor from '@shared/components/TiptapEditor.vue';
import { getUploadMixin } from '@shared/mixins/ContentBlockWithUpload';

export default {
  components: {
    TiptapEditor,
    ContentBlockScratchCardView,
  },
  mixins: [
    ContentBlockMixin,
    getUploadMixin('image', 'image'),
    getUploadMixin('scratchImage', 'scratchImage'),
  ],
  static: {
    data: {
      content: '',
      image: null,
      scratchImage: null,
      has_confetti: true,
    },
  },
  validations() {
    return {
      newData: {
        content: {
          required: requiredIf(() => !this.newData.image),
        },
        image: {
          required: requiredIf(() => (
            !this.newData.content.trim()
            || this.newData.content.trim() === '<p></p>'
          )),
        },
        scratchImage: {
          required,
        },
      },
    };
  },
  methods: {
    handleFile(file, field) {
      if (file.size > this.$constants.MAX_IMAGE_SIZE) {
        this.$buefy.dialog.alert(
          this.$t('messages.file_larger_than', { size: '5 Mo' }),
        );
        return;
      }

      const loader = this.$buefy.loading.open();
      this[`${field}UploadFile`](this.viewProps.store, 'image')
        .finally(() => loader.close());
    },
  },
  i18n: {
    messages: {
      en: {
        scratch_card: {
          title: 'Scratch Card',
          labels: {
            content: 'Text content',
            image: 'Image',
            scratch_image: 'Scratchable cover image',
            has_confetti: 'Enable confetti after scratch',
          },
          help: 'The text content or the image is required.',
        },
      },
      fr: {
        scratch_card: {
          title: 'Carte à gratter',
          labels: {
            content: 'Texte dévoilé',
            image: 'Image dévoilée',
            scratch_image: 'Image à gratter',
            has_confetti: 'Activer les confettis après grattage',
          },
          help: 'Vous devez obligatoirement renseigner un contenu à dévoiler : texte ou image.',
        },
      },
    },
  },
};
</script>

<template>
  <ContentBlockBase>
    <template #form>
      <h3 class="title is-5">
        {{ $t('scratch_card.title') }}
      </h3>

      <div class="field w-320">
        <b-field
          v-show="!newData.scratchImage"
          :label="$t('scratch_card.labels.scratch_image')"
          v-bind="$getErrorProps($v.newData.scratchImage, ['required'])"
        >
          <b-upload
            v-model="scratchImage.file"
            @input="handleFile($event, 'scratchImage')"
          >
            <b-button tag="a" class="button" icon-left="file-image" outlined>
              <span v-t="'buttons.select_image'" />
            </b-button>
          </b-upload>
        </b-field>
        <figure v-if="newData.scratchImage" class="image">
          <img :src="newData.scratchImage">
        </figure>
        <p v-show="newData.scratchImage" class="has-text-centered">
          <b-button
            type="is-text has-text-danger"
            @click="newData.scratchImage = ''"
          >
            <span v-t="'buttons.delete'" />
          </b-button>
        </p>
      </div>
      <div class="field">
        <b-switch v-model="newData.has_confetti" >
          {{ $t('scratch_card.labels.has_confetti') }}
        </b-switch>
      </div>

      <hr>

      <div class="notification mb-4">
        {{ $t('scratch_card.help') }}
      </div>

      <b-field
        :label-for="`content-${contentBlock.id}`"
        :label="$t('scratch_card.labels.content')"
        v-bind="$getErrorProps($v.newData.content, ['required'])"
      >
        <TiptapEditor
          :value="newData.content"
          @input="newData.content = $event"
          :field-id="`content-${contentBlock.id}`"
        />
      </b-field>

      <div class="field w-320">
        <b-field
          v-show="!newData.image"
          :label="$t('scratch_card.labels.image')"
          v-bind="$getErrorProps($v.newData.image, ['required'])"
        >
          <b-upload
            v-model="image.file"
            @input="handleFile($event, 'image')"
          >
            <b-button tag="a" class="button" icon-left="file-image" outlined>
              <span v-t="'buttons.select_image'" />
            </b-button>
          </b-upload>
        </b-field>
        <figure v-if="newData.image" class="image">
          <img :src="newData.image">
        </figure>
        <p v-show="newData.image" class="has-text-centered">
          <b-button
            type="is-text has-text-danger"
            @click="newData.image = ''"
          >
            <span v-t="'buttons.delete'" />
          </b-button>
        </p>
      </div>
    </template>
    <template #view>
      <ContentBlockScratchCardView v-bind="viewProps" :data="data" />
    </template>
  </ContentBlockBase>
</template>
